import { useState } from 'react';
import { z } from 'zod';

import { Modal } from '@/components';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { Input } from '@/components/Inputs';
import {
  useCreateNotificationMethodMutation,
  useUpdateNotificationMethodMutation,
} from '@/store/notification-settings/notification-settings.store';
import {
  type NotificationMethod,
  NotificationMethodType,
} from '@/store/notification-settings/notification-settings.types';

import s from '../NotificationsSettingsPage.module.scss';

type Props = {
  onClose: () => void;
  method?: NotificationMethod;
};

export const EditEmailModal = ({ onClose, method }: Props) => {
  const { mutate: createMethod } = useCreateNotificationMethodMutation();
  const { mutate: updateMethod } = useUpdateNotificationMethodMutation();
  const [draft, setDraft] = useState(method?.data ?? '');
  const [touched, setTouched] = useState(false);

  const trimmed = draft.trim();
  const error =
    trimmed && !z.string().email().safeParse(trimmed).success
      ? 'Please enter a valid email address.'
      : undefined;

  const save = () => {
    if (!trimmed || error) return;
    if (method) {
      updateMethod({ id: method.id, type: method.type, data: trimmed, active: method.active });
    } else {
      createMethod({ type: NotificationMethodType.EMAIL, data: trimmed });
    }
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Edit Email">
      <div className={s.modalBody}>
        <Input
          label="Email Address"
          placeholder="name@example.com"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setTouched(true);
          }}
          error={touched ? error : undefined}
        />
        <div className={s.modalActions}>
          <ButtonOutlinedSmall onClick={onClose}>Cancel</ButtonOutlinedSmall>
          <ButtonSmall onClick={save} disabled={!trimmed || !!error}>
            Save
          </ButtonSmall>
        </div>
      </div>
    </Modal>
  );
};
